// Workspace settings: the discovery category scope (`enabled_categories`).
// null = every category (the default); an explicit list narrows search, schema lookups
// and connect hints to those categories. It never blocks execution (connector policy does).
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { useToolCategories } from '../api/catalog'
import { errorMessage } from '../api/errors'
import { qk } from '../api/queries'
import type { ToolCategory, Workspace } from '../api/types'
import { useSession } from '../context/SessionContext'
import { PageHeader, QueryState } from '../components/ui'

function CategoryScopeForm({
  workspace,
  vocab,
  canEdit,
}: {
  workspace: Workspace
  vocab: ToolCategory[]
  canEdit: boolean
}) {
  const qc = useQueryClient()
  const initial = workspace.enabled_categories ?? null
  const [mode, setMode] = useState<'all' | 'some'>(initial === null ? 'all' : 'some')
  const [picked, setPicked] = useState<string[]>(initial ?? [])
  const [saved, setSaved] = useState(false)

  const save = useMutation({
    mutationFn: (enabled_categories: string[] | null) =>
      apiFetch<Workspace>(`/workspaces/${workspace.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled_categories }),
      }),
    onSuccess: (ws) => {
      qc.setQueryData(qk.workspace(workspace.id), ws)
      void qc.invalidateQueries({ queryKey: ['tool-search', workspace.id] })
      setSaved(true)
    },
  })

  const value = mode === 'all' ? null : picked
  const sameAsSaved =
    (value === null && initial === null) ||
    (value !== null && initial !== null && value.length === initial.length && value.every((v) => initial.includes(v)))
  const invalid = mode === 'some' && picked.length === 0
  // Slugs stored on the workspace that the vocabulary no longer lists stay visible.
  const extra = (initial ?? []).filter((s) => !vocab.some((c) => c.slug === s))
  const options = [...vocab.map((c) => ({ slug: c.slug, label: c.display_name })), ...extra.map((s) => ({ slug: s, label: s }))]

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (!canEdit || invalid || save.isPending) return
        setSaved(false)
        save.mutate(value)
      }}
      noValidate
    >
      <fieldset className="radio-row" disabled={!canEdit}>
        <legend className="sr-only">Category scope</legend>
        <label className="check">
          <input type="radio" name="cat-mode" checked={mode === 'all'} onChange={() => { setMode('all'); setSaved(false) }} />
          All categories <span className="cell-sub">(default)</span>
        </label>
        <label className="check">
          <input type="radio" name="cat-mode" checked={mode === 'some'} onChange={() => { setMode('some'); setSaved(false) }} />
          Only the categories I choose
        </label>
      </fieldset>

      {mode === 'some' && (
        <fieldset className="cat-grid" disabled={!canEdit} aria-label="Enabled categories">
          {options.map((c) => (
            <label key={c.slug} className="check">
              <input
                type="checkbox"
                checked={picked.includes(c.slug)}
                onChange={(e) => {
                  setSaved(false)
                  setPicked((p) => (e.target.checked ? [...p, c.slug] : p.filter((x) => x !== c.slug)))
                }}
              />
              {c.label}
            </label>
          ))}
        </fieldset>
      )}

      {invalid && <p className="form-error" role="alert">Pick at least one category, or choose “All categories”.</p>}
      {save.error ? <p className="form-error" role="alert">{errorMessage(save.error)}</p> : null}
      {saved && <p className="ok-note" role="status">Category scope saved.</p>}
      {canEdit ? (
        <div className="form-actions">
          <button type="submit" className="btn btn--primary" disabled={invalid || sameAsSaved || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      ) : (
        <p className="cell-sub">Only workspace owners and admins can change this.</p>
      )}
    </form>
  )
}

export function SettingsView() {
  const { workspaceId, session } = useSession()
  const role = session?.workspace.role
  const canEdit = role === 'owner' || role === 'admin'
  const ws = useQuery({
    queryKey: qk.workspace(workspaceId),
    queryFn: () => apiFetch<Workspace>(`/workspaces/${workspaceId}`),
    enabled: !!workspaceId,
  })
  const cats = useToolCategories()

  return (
    <div className="page page--narrow">
      <PageHeader title="Settings" description="Workspace-wide settings for how agents discover tools." />
      <QueryState
        isLoading={ws.isLoading || cats.isLoading}
        error={ws.error ?? cats.error}
        what="workspace settings"
        onRetry={() => {
          void ws.refetch()
          void cats.refetch()
        }}
      >
        {ws.data && (
          <section className="settings-section" aria-labelledby="cat-scope-h">
            <h2 id="cat-scope-h">Tool categories</h2>
            <p>
              Limit which toolkit categories agents in this workspace can discover (search, schema lookups and
              “available” connect hints). This narrows discovery only; use connector policy to block a toolkit.
            </p>
            <CategoryScopeForm
              workspace={ws.data}
              vocab={cats.categories}
              canEdit={canEdit}
            />
          </section>
        )}
      </QueryState>
    </div>
  )
}
